import { isFulfilled } from "@reduxjs/toolkit";
import { Form, Formik, FormikErrors } from "formik";
import { FC, MouseEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import {
  SUPER_FORM_CLIENT_ID,
  SUPER_FORM_ID,
  SUPER_FORM_NAME,
  SUPER_FORM_TEMPLATE_ID,
} from "../../../constants/app";
import { useImpersonation } from "../../../contexts/ImpersonationContext";
import { useReopenedStages } from "../../../hooks/useReopenedStages";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import { setTfnSuperformErrorMsgAction } from "../../../store/onboarding/slice";
import {
  checkDocStatus,
  getSuperFormFieldsThunk,
  submitTFNForm,
} from "../../../store/onboarding/thunk";
import {
  FLKDefaultValuesSection,
  FLKFormField,
  FlkFormField,
  FLKInitialValues,
  FLKRadioButtonValues,
  SuperFlkFormPayload,
  TGetFormFieldsPayload,
} from "../../../store/onboarding/types";
import { isSuperAdmin } from "../../../utils/roleHelper";
import Loader from "../../Loader";
import CustomButton from "../../UI/Buttons/CustomButton";
import Download from "../../UI/Icons/Download";
import LeftArrow from "../../UI/Icons/LeftArrow";
import RightArrow from "../../UI/Icons/RightArrow";
import Warning from "../../UI/Icons/Warning";
import DocumentUpload from "../../UI/Inputs/DocumentUpload";
import DynamicInput from "../../UI/Inputs/DynamicInput";
import DynamicRadioInput from "../../UI/Inputs/DynamicRadioInput";
import SplitInput from "../../UI/Inputs/SplitInput";
import ErrorMessage from "../../UI/Tooltips/ErrorMessage";
import "./superForm.scss";

/**
 * The `Superannuation` component is responsible for rendering and managing the Superannuation Declaration form.
 * It integrates with Redux for state management and uses Formik for form handling. The component dynamically
 * generates form fields based on data fetched from the backend and handles various user interactions, such as
 * form submission, file uploads, and PDF downloads.
 *
 * @component
 *
 * @returns {JSX.Element} The rendered Superannuation Declaration form and related UI elements.
 *
 * @remarks
 * - This component uses Redux to fetch and manage form data (`superFormFields`) and user information (`user`).
 * - Formik is used for form validation and submission.
 * - The component dynamically renders form fields based on the `superFormFields` array.
 * - It supports various field types, including text, split-input, radio-group, and file upload.
 * - External software is used to generate the official Tax File Number Declaration document.
 *
 * @example
 * ```tsx
 * <Superannuation />
 * ```
 *
 * @dependencies
 * - Redux actions: `getSuperFormFieldsThunk`, `submitTFNForm`, `checkDocStatus`
 * - Formik for form handling
 * - Custom components: `DynamicInput`, `SplitInput`, `DynamicRadioInput`, `DocumentUpload`, `CustomButton`
 *
 * @state
 * - `superFormFields` (`FLKFormField[]`): Stores the dynamically generated form fields.
 * - `superInitialValues` (`FLKInitialValues`): Stores the initial values for the form fields.
 * - `isDisabled` (`boolean`): Tracks whether certain buttons are disabled.
 * - `serverErrorMsg` (`string`): Stores error messages from the server.
 * - `FlkClientUrl` (`string`): Stores the URL for the external client interface.
 * - `FlkPdfUrl` (`string`): Stores the URL for the generated PDF document.
 * - `flkSuperTemplateName` (`string`): Stores the name of the selected superannuation template.
 * - `flkSuperTemplateValue` (`string`): Stores the value of the selected superannuation template.
 *
 * @methods
 * - `loadSuperFormFields`: Fetches and initializes the form fields.
 * - `prepareInitialValues`: Prepares the initial values for the form fields.
 * - `finalUserInput`: Handles user input for split-input field types.
 * - `handleSetFormValues`: Updates the state with the user's input for a specific field.
 * - `handleSuperTemplate`: Handles changes to the superannuation template.
 * - `handleFormSubmitValue`: Submits the form data to the backend.
 * - `handleDocStatus`: Checks the status of the candidate's document.
 * - `EditSuperForm`: Enables editing of the Superannuation form.
 * - `handleDownloadPdf`: Triggers the download of the generated PDF document.
 * - `handleFileSelect`: Handles file uploads and updates the corresponding form field.
 */
const Superannuation: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const superFormDataResponse = useSelector(
    (state) => state.onboarding.superForm
  );
  const user = useSelector((state) => state.auth.user);
  const errorMsg = useSelector((state) => state.onboarding.errorMessage)

  // State Declaration
  const [superFormFields, setSuperFormFields] = useState<FLKFormField[]>([]);
  const [superInitialValues, setSuperInitialValues] =
    useState<FLKInitialValues>({});
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [serverErrorMsg, setServerErrorMsg] = useState<string>("");
  const [FlkClientUrl, setFlkClientUrl] = useState<string>("");
  const [FlkPdfUrl, setFlkPdfUrl] = useState<string>("");
  const [flkSuperTemplateName, setFlkSuperTemplateName] = useState<string>("");
  const [flkSuperTemplateValue, setFlkSuperTemplateValue] =
    useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { isImpersonating } = useImpersonation();

  const superAdminCandidateData = useSelector(
      (state) => state.superadmin.reviewCandidateData);
  const isUserSuperAdmin = isSuperAdmin(user);
  const { isStageReopenedFn, reopenedSections } = useReopenedStages();
  const isStage3Reopen = isStageReopenedFn(3);

  // Variable Devlaration
  const initialValues: FLKInitialValues = {};
  const getPayload: TGetFormFieldsPayload = {
    template_id: SUPER_FORM_TEMPLATE_ID,
    is_super_form: "1",
    is_tfn_form: "0",
    candidate_id: JSON.stringify(isUserSuperAdmin ? superAdminCandidateData?.id :  user?.id),
  };

  // Ref to track if loadTfnFormFields has already been called
  const isInitialLoad = useRef(false);

  //Loading initial form fields
  const loadSuperFormFields = (): void => {
    dispatch(getSuperFormFieldsThunk(getPayload));
  };

  useEffect(() => {
    if (!isInitialLoad.current) {
      setIsLoading(true);
      loadSuperFormFields();
      isInitialLoad.current = true; // Mark as executed
    } else if (superFormDataResponse !== null) {
      let superFormData: FLKFormField[] = [];
      let s3PdfUrl: string = "";
      let signingUrl: string = "";
      s3PdfUrl = superFormDataResponse?.data?.s3_pdf_url || "";
      signingUrl = superFormDataResponse?.signingUrl || "";
      if (s3PdfUrl?.length > 0) {
        setFlkPdfUrl(s3PdfUrl);
        document.querySelector(".super-form")?.classList.add("d-none");
        setIsLoading(false);
      } else if(signingUrl && signingUrl?.length > 0) {
        setFlkClientUrl(isImpersonating ? "" : signingUrl);
        superFormData = superFormDataResponse?.data?.fields ? JSON.parse(superFormDataResponse?.data?.fields) : [];
        prepareInitialValues(superFormData as FLKFormField[]);
        setSuperFormFields(superFormData);
        setIsLoading(false);
      } else {
        superFormData = superFormDataResponse?.data?.fields
          ? JSON.parse(superFormDataResponse?.data?.fields)
          : [];
        prepareInitialValues(superFormData);
        setSuperFormFields(superFormData);
        setIsLoading(false);
        document.querySelector(".flk-form")?.classList.remove("max-w-1200px");
        document.querySelector(".flk-form")?.classList.add("max-w-960px");
      }
    }
  }, [superFormDataResponse]);

  // Function to prepare initial values for the form fields
  const prepareInitialValues = (fields: FLKFormField[]) => {
    fields?.forEach((field: FLKFormField) => {
      if (!field.type?.includes("html")) {
        if (field.type === "split-input") {
          const splitLength = Array(field?.boxCount).fill(null);
          splitLength.forEach((_: null, index: number) => {
            initialValues[`${field.name}-${index}`] = field.value
              ? field.value?.[index]
              : "";
          });
          initialValues[field.name] = field.value;
        } else {
          initialValues[field.name] = field.value;
        }
        
        // Set the super template value if this field has a superFormTemplateId and has a value
        if (field.superFormTemplateId && field.value) {
          handleSuperTemplate(field, field.value);
        }
      }
    });
    setSuperInitialValues(initialValues);
  };

  // Function to handle final user input for split input field type
  const finalUserInput = (splitLength: Array<null>, name: string): string => {
    const values: string[] = [];
    splitLength.forEach((_, index: number) => {
      const fieldInputValue = (
        document.querySelector<HTMLInputElement>(`[name="${name}-${index}"]`)
          ?.value || ""
      ).trim();
      values.push(fieldInputValue !== "" ? fieldInputValue : " ");
    });
    return values.join("").toUpperCase();
  };

  // Function to handle form field values update the state
  const handleSetFormValues = (fieldName: string, value: string) => {
    let currentField = superFormFields.find(
      (field: FLKFormField) => field?.name === fieldName
    );

    setSuperFormFields((prevFields: FLKFormField[]) =>
      prevFields.map((field: FLKFormField) => {
        if (field?.name === fieldName) {
          if (field?.type === "split-input") {
            const splitLength = Array(field?.boxCount).fill(null);
            const finalSplitInputValue = finalUserInput(
              splitLength,
              field?.name
            );
            return { ...field, value: finalSplitInputValue };
          } else if (currentField?.superFormTemplateId) {
            // handleSuperTemplate(currentField, value);
            return { ...field, value };
          } else {
            return { ...field, value };
          }
        }
        return field;
      })
    );

    if (currentField?.superFormTemplateId) {
      handleSuperTemplate(currentField, value);
    }
  };

  const handleSuperTemplate = (
    fieldData: { values: Array<{ value: string; tfnTemplateName: string }> },
    currentValue: string
  ) => {
    setFlkSuperTemplateValue(currentValue);
    setFlkSuperTemplateName(() => {
      const selectedOption = fieldData?.values.find(
        (option) => option?.value === currentValue
      );
      const templateName = selectedOption?.tfnTemplateName || "";
      return templateName;
    });
  };

  // Function to handle form submission
  const handleFormSubmitValue = async (): Promise<void> => {
    dispatch(setTfnSuperformErrorMsgAction(''))
    try {
      setIsLoading(true);
      const superFormData: FlkFormField[] = [
        {
          fields: superFormFields,
          isTFNForm: 0,
          isSuperForm: 1,
          label: SUPER_FORM_NAME,
          formId: SUPER_FORM_ID,
          clientId: SUPER_FORM_CLIENT_ID,
          candidateId: JSON.stringify(user?.id),
        },
      ];

      const tfnFormPayloads: SuperFlkFormPayload = {
        data: JSON.stringify(superFormData),
        candidate_id: JSON.stringify(user?.id),
        form_name: SUPER_FORM_NAME,
        is_active_super_form: "1",
        is_active_tfn_form: "0",
        is_super_form_changed: "1",
        is_tfn_form_changed: "0",
        template_id: getPayload.template_id ?? "",
        flk_super_template_name: flkSuperTemplateName,
      };

      const result = await dispatch(submitTFNForm(tfnFormPayloads));
      if (isFulfilled(result)) {
        const submitResponse = result.payload.data;
        if (result?.payload?.statusCode === 200) {
          setFlkClientUrl(submitResponse?.flk_client_url);
          document.querySelector(".super-form")?.classList.add("d-none");
          setIsLoading(false);
          document.querySelector(".flk-form")?.classList.add("max-w-1200px");
          document.querySelector(".flk-form")?.classList.remove("max-w-960px");
        }
      } else {
        setIsLoading(false);
        // setServerErrorMsg(
        //   "Could not connect to services at the moment. Please try again. Thanks."
        // );
        setIsDisabled(false);
      }
    } catch (error) {
      setIsLoading(false);
      setIsDisabled(false);
      throw new Error(
        "Could not connect to services at the moment. Please try again. Thanks."
      );
    }
  };

  // Function to handle checking the candidate document status
  const handleDocStatus = async (): Promise<void> => {
    setIsLoading(true);
    const docStatusPayload = {
      candidate_id: JSON.stringify(user?.id),
      template_id: getPayload.template_id ?? "",
    };
    const docStatusResponse = await dispatch(checkDocStatus(docStatusPayload));
    if (isFulfilled(docStatusResponse)) {
      const docStatus = docStatusResponse.payload;
      if (docStatus?.data.statusCode === 200 && docStatus?.data.pdf_url.length > 0) {
        setFlkClientUrl("");
        setFlkPdfUrl(docStatus?.data.pdf_url);
        setIsLoading(false);

        if (isStage3Reopen && reopenedSections.length > 0) {
          const currentIndex = stage3Keys.indexOf("superannuation_form");
          const stage3ReopenedSections = reopenedSections.filter(
            (key) => stage3Keys.includes(key)
          );
          const nextSection = stage3ReopenedSections.find(
            (key) => stage3Keys.indexOf(key) > currentIndex
          );
          if (nextSection) {
            const route = sectionRouteMap[nextSection];
            navigate(
              isUserSuperAdmin
                ? `/admin/candidate-profile/${route}`
                : `/candidate/my-profile/${route}`
            );
            return;
          }
        }
      } else {
        setIsLoading(false);
        toast.warn(
          docStatus?.data.message ||
            "Please sign the document before proceeding to the next step."
        );
        setServerErrorMsg(
          "Please sign the document before proceeding to the next step."
        );
      }
    }
  };

  // Function to handle editing the Super form
  const EditSuperForm = () => {
    setFlkClientUrl("");
    setServerErrorMsg("");
    document.querySelector(".super-form")?.classList.remove("d-none");
    document.querySelector(".flk-form")?.classList.remove("max-w-1200px");
    document.querySelector(".flk-form")?.classList.add("max-w-960px");
  };

  // Function to handle PDF download
  const handleDownloadPdf = (pdfUrl: string) => {
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.target = "_blank";
    link.setAttribute("download", "Superannuation_Declaration.pdf"); // Ensure the download attribute is set
    document.body.appendChild(link); // Append the link to the DOM
    link.click(); // Trigger the download
    document.body.removeChild(link); // Clean up by removing the link
  };

  const handleFileSelect = (file: any) => {
    const s3Url = file?.data?.document;
    if (!s3Url) return; // Ensure s3Url is defined before proceeding
    setSuperFormFields((prevFields: FLKFormField[]) =>
      prevFields.map((field: FLKFormField) => {
        if (field.docType === file?.data?.document_type) {
          return { ...field, value: s3Url || field.value }; // Retain the existing value if s3Url is undefined
        }
        return field;
      })
    );
  };

  //Handled error summary to show the error list to the user since submit button is disable until all the field validation is passed
  const errorSummary = (errors: FormikErrors<FLKInitialValues>) => {
    if (Object.keys(errors).length === 0) return null;
    const uniqueErrors = Array.from(new Set(Object.values(errors)));
    return (
      <div className="error-list">
        <h3 className="fs-20 fw-500 text-red">Error Summary</h3>
        <ul className="fs-16 fw-400 text-black">
          {uniqueErrors.map((error, index) => (
            <li key={index}>
              <ErrorMessage message={String(error)} />
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const handleSuperFormSectionC = async (
    e: React.ChangeEvent<HTMLInputElement>,
    ress: FLKFormField,
    setErrors: any,
    setFieldError: any,
    setFieldTouched: any
  ) => {
    setErrors({});
    const selectedValue = e.target.value;
    const selectedOption = ress?.values.find(
      (option: FLKRadioButtonValues) => option?.value === selectedValue
    );
    const childFieldList = selectedOption?.childFieldList || [];
    const isFilledByEmployer = selectedOption?.filledByEmployer || false;
    if (isFilledByEmployer === true) {
      
      childFieldList.forEach((childField: FLKDefaultValuesSection) => {
        const fieldName = childField.field;
        const fieldValue = childField.defaultValue || "";
        setSuperFormFields((prevFields: FLKFormField[]) =>
          prevFields.map((field: FLKFormField) => {
            setFieldError(fieldName, "");
            setFieldTouched(fieldName, false);
            if (field.name === fieldName) {
              return { ...field, value: fieldValue };
            }
            return field;
          })
        );

        const splitLength = Array(childField?.boxCount).fill(null);
        splitLength.forEach((_: number | null, index: number) => {
          setTimeout(() => {
            const element = document.getElementById(`${fieldName}-${index}`);
            if (element) {
              (element as HTMLInputElement).value = fieldValue[index] || "";
              
            }

            setSuperInitialValues((prevValues: FLKInitialValues) => {
              const updatedValues = { ...prevValues };
              Object.keys(updatedValues).forEach((key) => {
                if (key.startsWith(fieldName)) {
                  setFieldError(fieldName, "");
                  setFieldTouched(fieldName, false);
                  updatedValues[key] =
                    fieldValue[parseInt(key.split("-")[1], 10)] || "";
                }
              });
              return updatedValues;
            });
          }, 0);
        });
      });
    }
  };

   const stage3Keys = [
      "tfn_form",
      "superannuation_form",
      "contract_form",
      "casual_fair_work_statement",
      "bankAccount",
      "emergency_info",
    ];
    const sectionRouteMap: Record<string, string> = {
      tfn_form: "tfn-declaration",
      superannuation_form: "superannuation",
      contract_form: "contract-form",
      casual_fair_work_statement: "casual-and-fair-work-statement",
      bankAccount: "bank-account",
      emergency_info: "next-of-kin",
    };
  
   
  
    const handleNext = () => {
      dispatch(setTfnSuperformErrorMsgAction(''));
  
      if (isStage3Reopen && reopenedSections.length > 0) {
        const currentIndex = stage3Keys.indexOf("superannuation_form");
        const stage3ReopenedSections = reopenedSections.filter(
          (key) => stage3Keys.includes(key)
        );
        const nextSection = stage3ReopenedSections.find(
          (key) => stage3Keys.indexOf(key) > currentIndex
        );
        if (nextSection) {
          const route = sectionRouteMap[nextSection];
          navigate(
            isUserSuperAdmin
              ? `/admin/candidate-profile/${route}`
              : `/candidate/my-profile/${route}`
          );
          return;
        }
      }
  
      navigate(isUserSuperAdmin ? "/admin/candidate-profile/contract-form" : "/candidate/my-profile/contract-form");
    };

  // const handleNext = () => {
  //   dispatch(setTfnSuperformErrorMsgAction(''));
  //   // navigate(isAdmin ? "/admin/candidate-profile/superannuation" : "/candidate/my-profile/superannuation");
  //   navigate(isUserSuperAdmin ? "/admin/candidate-profile/contract-form" : "/candidate/my-profile/contract-form");
  // };

  useEffect(() => {
    if(FlkClientUrl && FlkClientUrl.length > 0) {
      document.querySelector(".flk-form")?.classList.add("max-w-1200px");
      document.querySelector(".flk-form")?.classList.remove("max-w-960px");
      setIsDisabled(false);
    } else {
      document.querySelector(".flk-form")?.classList.remove("max-w-1200px");
      document.querySelector(".flk-form")?.classList.add("max-w-960px");
      setIsDisabled(true);
    }
  }, [FlkClientUrl]);

  return (
    <div className="d-flex flex-column gap-20">
      {/* <div className="fs-30 fw-400 text-black">Review Contract</div>
      <div className="d-flex flex-column gap-10">
        <div className="fs-16 fw-400 text-black">
          Before you begin filling in the documents, please download and read
          the below contract that you will be required to sign in Step 3 of this
          stage.
        </div>
        <div className="d-flex">
          <CustomButton
            variant={"primary"}
            type="button"
            className="btn-theme bg-off-white text-black"
            onClick={openContractForm}
          >
            <Download />
            Contract.pdf
          </CustomButton>
        </div>
      </div>
      <div className="h-10px"></div> */}
      <div className="fs-30 fw-400 text-black">Superannuation Declaration</div>
      { !FlkPdfUrl ? <div className="bg-teal-light py-10px px-15px radius-8px d-flex gap-6">
        <Warning />
        <div className="fs-13 fw-400 text-black">
          The data collected from this form will be inserted into the official
          Tax File Number Declaration document using external software.
        </div>
      </div> : "" }
      { isImpersonating && FlkPdfUrl?.length === 0 && 
        <div className={`bg-teal-light px-15px py-10px radius-8px d-flex gap-6`}>
          <Warning />
          <div className="fs-13 fw-400 text-black lh-sm">
            You are currently impersonating a candidate. You cannot sign the document while in impersonation mode.
          </div>
        </div>
      }
      <Formik
        initialValues={superInitialValues}
        validateOnBlur={true}
        validateOnChange={true}
        onSubmit={(values) => {
          handleFormSubmitValue();
        }}
      >
        {({
          errors,
          touched,
          isValidating,
          isValid,
          setFieldValue,
          setFieldError,
          setFieldTouched,
          setErrors
        }) => (
          <>
            {isLoading && (
              <div className="transparent-preloader">
                <Loader />
              </div>
            )}
            <div className="super-form">
              <Form>
                {superFormFields?.length > 0 && FlkClientUrl?.length === 0
                  ? superFormFields.map(
                      (superForm: FLKFormField, index: number) => (
                        <div
                          key={superForm.name}
                          className={
                            !superForm?.parentValue ||
                            superForm?.parentValue === flkSuperTemplateValue
                              ? superForm?.containerClass
                              : ""
                          }
                        >
                          {superForm.type === "html" &&
                            superForm?.isInfoText !== true &&
                            (!superForm?.parentValue ||
                              superForm?.parentValue ===
                                flkSuperTemplateValue) && (
                              <div
                                key={index}
                                className={superForm?.lableClass}
                                dangerouslySetInnerHTML={{
                                  __html: superForm.label,
                                }}
                              ></div>
                            )}

                          <div className={superForm?.className}>
                            {superForm.type === "file" &&
                              (!superForm?.parentValue ||
                                superForm?.parentValue ===
                                  flkSuperTemplateValue) && (
                                <DocumentUpload
                                  type={superForm?.docType}
                                  onFileSelect={handleFileSelect}
                                  required={true}
                                />
                              )}

                            {superForm.type === "text" &&
                              (!superForm?.parentValue ||
                                superForm?.parentValue ===
                                  flkSuperTemplateValue) && (
                                <>
                                  <label>{superForm?.label}</label>
                                  <DynamicInput
                                    type="text"
                                    name={superForm.name}
                                    className="form-control"
                                    maxLength={superForm.maxLength}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>
                                    ) =>
                                      handleSetFormValues(
                                        superForm?.name,
                                        e.target.value
                                      )
                                    }
                                    error={errors}
                                    placeholder={superForm?.placeholder}
                                    allData={{
                                      ...superForm,
                                      type: superForm.type as
                                        | "number"
                                        | "text"
                                        | undefined,
                                      validations: superForm.validations.map(
                                        (validation) => ({
                                          ...validation,
                                          params: [validation.params[0]], // Ensure params is a tuple with one string
                                        })
                                      ),
                                    }}
                                    value={superForm?.value}
                                  />
                                </>
                              )}

                            {superForm.type === "split-input" &&
                              (!superForm?.parentValue ||
                                superForm?.parentValue ===
                                  flkSuperTemplateValue) && (
                                <SplitInput
                                  name={superForm.name}
                                  label={superForm.label}
                                  type={superForm.type}
                                  className={superForm.className}
                                  allData={superForm}
                                  maxlength={superForm.maxLength}
                                  onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                  ) =>
                                    handleSetFormValues(
                                      superForm?.name,
                                      e.target.value
                                    )
                                  }
                                  error={errors}
                                  setFieldValue={setFieldValue}
                                  value={superForm?.value}
                                  selectedValue={superForm?.value}
                                />
                              )}

                            {superForm.type === "radio-group" &&
                              (!superForm?.parentValue ||
                                superForm?.parentValue ===
                                  flkSuperTemplateValue) && (
                                <DynamicRadioInput
                                  option={superForm?.values}
                                  name={superForm?.name}
                                  allData={superForm}
                                  formik={superForm}
                                  selectedValue={superForm?.value}
                                  value={superForm?.value}
                                  error={errors}
                                  onChange={() =>
                                    handleSetFormValues(
                                      superForm?.name,
                                      superForm?.value
                                    )
                                  }
                                  setFieldValue={setFieldValue}
                                  setFieldTouched={setFieldTouched}
                                  setSuperInitialValues={(
                                    values: string | number | null
                                  ) =>
                                    setSuperInitialValues((prev) => ({
                                      ...prev,
                                      [String(values)]: String(values),
                                    }))
                                  }
                                  handleClick={(
                                    event: MouseEvent<HTMLInputElement>
                                  ) => {
                                    const target =
                                      event.target as HTMLInputElement;
                                    handleSuperFormSectionC(
                                      {
                                        target,
                                      } as React.ChangeEvent<HTMLInputElement>,
                                      superForm,
                                      setErrors,
                                      setFieldError,
                                      setFieldTouched
                                    );
                                  }}
                                />
                              )}

                            {superForm?.isInfoText === true &&
                              (!superForm?.parentValue ||
                                superForm?.parentValue ===
                                  flkSuperTemplateValue) && (
                                <div className="bg-teal-light py-10px px-15px radius-8px d-flex gap-6 my-3">
                                  <Warning />
                                  <div
                                    className="fs-13 fw-400 text-black"
                                    dangerouslySetInnerHTML={{
                                      __html: superForm.label,
                                    }}
                                  ></div>
                                </div>
                              )}
                          </div>
                        </div>
                      )
                    )
                  : null}

                {superFormFields?.length > 0 && FlkClientUrl?.length === 0 && !isImpersonating? (
                  <div className="d-flex flex-column flex-md-row">
                    <CustomButton
                      type="submit"
                      variant="primary"
                      disabled={!isValid}
                      className={`btn-theme ${
                        !isValid
                          ? "text-grey-medium bg-off-white"
                          : "bg-teal text-black"
                      }`}
                    >
                      Next{" "}
                      <RightArrow color={!isValid ? "#757575" : "#000000"} />
                    </CustomButton>
                  </div>
                ) : null}

                {/* {errors ? <>{errorSummary(errors)}</> : ""} */}
              </Form>
              { errorMsg !== "" ? <ErrorMessage className="mt-3" message={errorMsg} /> : <ErrorMessage message={serverErrorMsg} /> }
            </div>

            <div>
              {FlkClientUrl?.length > 0 ? (
                <>

                {
                  !isImpersonating && 
                  <iframe
                    src={FlkClientUrl}
                    title="flk-client"
                    className="flk-client flk-iframe"
                    width={100}
                  ></iframe>
                }
                  

                  <div className="button-group justify-content-between mt-2">
                    <CustomButton
                      type="button"
                      variant="primary"
                      disabled={isDisabled}
                      className={`btn-theme ${
                        isDisabled
                          ? "text-grey-medium bg-off-white"
                          : "bg-teal text-black"
                      }`}
                      onClick={EditSuperForm}
                    >
                      <LeftArrow /> Edit Form
                    </CustomButton>
                    { !isImpersonating && 
                      <CustomButton
                        type="button"
                        variant="primary"
                        disabled={isDisabled}
                        className={`btn-theme ${
                          isDisabled
                            ? "text-grey-medium bg-off-white"
                            : "bg-teal text-black"
                        }`}
                        onClick={handleDocStatus}
                      >
                        Next{" "}
                        <RightArrow color={isDisabled ? "#757575" : "#000000"} />
                      </CustomButton>
                    }
                  </div>
                </>
              ) : (
                ""
              )}

              {/* {errorSummary(errors)} */}

              {FlkPdfUrl?.length > 0 && (
                <div className="d-flex flex-column gap-20">
                  <div className="fw-400 fs-16">Please click the link below to download a copy of this document for your records.</div>
                  <div className="d-flex">
                    <CustomButton
                      type="button"
                      className="btn-theme bg-teal text-white"
                      variant="primary"
                      onClick={() => handleDownloadPdf(FlkPdfUrl)}
                    >
                      <Download />
                      <div className="text-black">
                        Download Superannuation Declaration PDF
                      </div>
                    </CustomButton>
                  </div>
                  <div className="d-flex">
                    <CustomButton
                      type="submit"
                      variant="primary"
                      onClick={handleNext}
                      className="btn-theme bg-teal text-black  mt-20"
                    >
                      Next
                      <RightArrow color={"#000000"} />
                    </CustomButton>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </Formik>
    </div>
  );
};

export default Superannuation;
